/* eslint-disable formatjs/no-literal-string-in-jsx */
import React, { Suspense, lazy, useEffect, useMemo, useState } from "react";

import IntercomSDK from "@intercom/messenger-js-sdk";
import { QueryClientProvider } from "@tanstack/react-query";
import moment from "moment";
import momentDurationFormatSetup from "moment-duration-format";
import { createRoot } from "react-dom/client";
import { ErrorBoundary } from "react-error-boundary";
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
  useOutletContext,
  useParams,
} from "react-router-dom";

import { CfLoader } from "@/shared/components/common/CfLoader/CfLoader";

import resetReducer from "./reducers/reset.reducer";
import signupReducer from "./reducers/signup.reducer";

import { fetchConfig } from "./app.config";
import { ChunkErrorFallback as ChunkErrorFallbackOriginal } from "./common/components/ChunkErrorFallback/ChunkErrorFallback";
import { FeatureFlagProvider } from "./common/providers/FeatureFlagProvider/FeatureFlagProvider";
import { clearAllUserData } from "./common/services/auth/Auth.service";
import LoginWrapper from "./core/login/containers/LoginWrapper/LoginWrapper";
import { NavbarWrapper } from "./core/navbar/containers/NavbarWrapper/NavbarWrapper";
import ResetWrapper from "./core/reset/containers/ResetWrapper/ResetWrapper";
import { SignupWrapper } from "./core/signup/containers/SignupWrapper/SignupWrapper";
import {
  FarmSettingsTo,
  FarmTo,
  GeoJsonPolygon,
  getFarmSettings,
  getUserFarms,
} from "./generated/api/agroevidence";
import { setup } from "./shared/api/client";
import CfApp from "./shared/containers/CfApp/CfApp";
import { FarmSettingsProvider } from "./shared/providers/FarmSettingsProvider";
import { queryClient } from "./shared/query-client";
import { GoogleAnalyticsService } from "./shared/services/GoogleAnalytics.service";
import { JwtService } from "./shared/services/Jwt.service";
import Localization, {
  LANGUAGES,
} from "./shared/services/Localization.service";
import LocalStorage from "./shared/services/LocalStorage.service";
import { logger } from "./shared/services/SentryLogger.service";
import "./assets/index.css";

import "moment/dist/locale/cs";
import "moment/dist/locale/es";
import "moment/dist/locale/hu";
import "moment/dist/locale/lt";
import "moment/dist/locale/pl";
import "moment/dist/locale/ro";
import "moment/dist/locale/sk";

setup();

const LazyAdminModule = lazy(() => import("./admin/AdminModule"));
const LazyMapModule = lazy(() => import("./map/map.module"));
const LazyUserModule = lazy(() => import("./core/user/UserModule"));
const LazyNotificationsModule = lazy(
  () => import("./notifications/NotificationsModule"),
);
const LazySensorsModule = lazy(() => import("./sensors/SensorsModule"));
const LazyReportsModule = lazy(
  () => import("./evidence/reports/ReportsModule"),
);
const LazyActionsModule = lazy(
  () => import("./evidence/actions/ActionsModule"),
);
const LazyActionsV2Module = lazy(
  () => import("./evidence/actions-v2/ActionsV2Module"),
);
const LazyCataloguesModule = lazy(
  () => import("./catalogues/containers/CataloguesModule"),
);
const LazyStoresModule = lazy(() => import("./evidence/stores/StoresModule"));
const LazyTelematicsModule = lazy(
  () => import("./telematics/TelematicsModule"),
);
const LazyParcelsModule = lazy(() => import("./core/parcels/ParcelsModule"));
const LazyParcelsV2Module = lazy(
  () => import("./core/parcels/ParcelsV2Module"),
);
const LazyPrecisionFarmingModule = lazy(
  () => import("./precision-farming/PrecisionFarmingModule"),
);
const IrrigationModule = lazy(() => import("./irrigation/IrrigationModule"));
const LazyOAuthModule = lazy(() => import("./oauth/OAuthModule"));

// @ts-expect-error it worked fine before, leaving it like this until we move to something else
momentDurationFormatSetup(moment);

type AsyncResult<T> =
  | ({ status: "success" } & T)
  | { status: "error" }
  | { status: "pending" };

type Config = {
  api: string;
  environment: string;
};

export type ResolvedProps = {
  farmId: string;
  farmBbox?: GeoJsonPolygon;
  farm: {
    id: string;
    name: string;
    countryCode: string;
    boundingBox?: GeoJsonPolygon;
    isAdmin: boolean;
  };
  farms: Array<Omit<ResolvedProps["farm"], "isAdmin">>;
  config: {
    api: string;
    environment: string;
  };
  apiProps: {
    farmId: string;
    beOpts: string;
    langId: null;
  };
};

const isChunkError = (e: unknown) =>
  e instanceof Error &&
  /ChunkLoadError|Loading chunk \d+ failed|dynamically imported module/i.test(
    e.message,
  );

function ChunkErrorFallback({ error }: { error: unknown }) {
  useEffect(() => {
    if (isChunkError(error)) {
      const key = "chunk-reload-once";
      if (sessionStorage.getItem(key) !== "1") {
        sessionStorage.setItem(key, "1");
        window.location.reload();
      }
    }
  }, [error]);

  try {
    return <ChunkErrorFallbackOriginal error={error as Error} />;
  } catch {
    return (
      <div style={{ padding: 16 }}>
        <div>Something went wrong.</div>
        <button onClick={() => window.location.reload()}>Reload</button>
      </div>
    );
  }
}

const useConfig = () => {
  const [config, setConfig] = useState<Config>();

  useEffect(() => {
    fetchConfig().then((jsonConfig) => {
      setConfig({
        api: jsonConfig.BACKEND_OPTIONS,
        environment: jsonConfig.ENVIRONMENT,
      });
    });
  }, []);

  return config;
};

const useFarms = () => {
  const [farms, setFarms] = useState<AsyncResult<{ farms: FarmTo[] }>>({
    status: "pending",
  });

  useEffect(() => {
    if (farms.status === "success") return;

    getUserFarms()
      .then(({ data: farmsData }) => {
        setFarms({
          status: "success",
          farms: farmsData.toSorted(({ name: aName }, { name: bName }) =>
            aName && bName ? aName.localeCompare(bName) : 0,
          ),
        });
        LocalStorage.saveToLocalStorage(farmsData, "farms");
      })
      .catch(() => {
        setFarms({ status: "error" });
      });
  }, [farms.status]);

  return farms;
};

const useFarmSettings = (farmId?: string): FarmSettingsTo | undefined => {
  const [settings, setSettings] = useState<
    AsyncResult<{ settings: FarmSettingsTo }>
  >({ status: "pending" });

  useEffect(() => {
    if (!farmId) return;

    if (settings.status === "success") return;

    getFarmSettings(farmId)
      .then(({ data }) => {
        setSettings({ status: "success", settings: data });
        LocalStorage.saveToLocalStorage(data, "farmSettings");
      })
      .catch(() => {
        setSettings({ status: "error" });
      });
  }, [farmId, settings.status]);

  return settings.status === "success" ? settings.settings : undefined;
};

const useUser = ():
  | {
      authorities: ("ROLE_USER" | "ROLE_ADMIN")[];
      email: string;
      firstName: string;
      id: string;
      lastName: string;
    }
  | {
      authorities?: never;
      email?: never;
      firstName?: never;
      id?: never;
      lastName?: never;
    } => {
  const { authorities, email, firstName, id, lastName } =
    LocalStorage.loadFromLocalStorage("user") ?? {};
  return { email, firstName, id, lastName, authorities };
};

const InitializeIntercom = ({
  children,
  currentFarm,
  farms,
}: React.PropsWithChildren<{
  currentFarm: ResolvedProps["farm"];
  farms: ResolvedProps["farms"];
}>) => {
  const { email, firstName, id, lastName } = useUser();

  useEffect(() => {
    if (!process.env.INTERCOM_APP_ID) return;
    if (JwtService.isImpersonatedToken()) return;
    if (!email) return;

    IntercomSDK({
      hide_default_launcher: true,
      app_id: process.env.INTERCOM_APP_ID,
      company: currentFarm,
      // @ts-expect-error TODO fix
      companies: farms,
      email,
      name: `${firstName} ${lastName}`,
      user_id: id,
    });
  }, [currentFarm, email, farms, firstName, id, lastName]);

  return <>{children}</>;
};

const InitializeSentry = ({ children }: React.PropsWithChildren<unknown>) => {
  const { email, firstName, id, lastName } = useUser();

  useEffect(() => {
    if (email) {
      logger.setUser({
        email,
        id,
        username: `${firstName} ${lastName}`,
      });
    } else {
      logger.setUser(null);
    }
  }, [email, firstName, id, lastName]);

  return <>{children}</>;
};

const InitializeLanguage = ({ children }: React.PropsWithChildren<unknown>) => {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    Localization.initLangInLocalStorage();
    moment.locale(Localization.getLang());
    setReady(true);
  }, []);

  return ready ? <>{children}</> : <CfLoader size={20} />;
};

const InitializeGoogleAnalytics = ({
  children,
}: React.PropsWithChildren<unknown>) => {
  const user = useUser();

  useEffect(() => {
    if (user.email) {
      GoogleAnalyticsService.initialize(user);
    }
  }, [user]);

  return <>{children}</>;
};

const ConfigResolver = ({
  children,
}: {
  children: (config: Config) => JSX.Element;
}) => {
  const config = useConfig();
  if (!config) return <CfLoader size={20} />;
  return children(config);
};

type BootstrapCtx = {
  config: Config;
  farms: FarmTo[];
  user: ReturnType<typeof useUser>;
};

const ResolverBootstrap = () => {
  const config = useConfig();
  const farmsRes = useFarms();
  const user = useUser();

  if (!config || farmsRes.status === "pending") {
    return <CfLoader size={20} />;
  }
  if (farmsRes.status === "error") return <Navigate replace to="/login" />;

  if (!user.id) {
    clearAllUserData();
    return <Navigate replace to="/login" />;
  }

  return (
    <Outlet
      context={{ config, farms: farmsRes.farms, user } satisfies BootstrapCtx}
    />
  );
};

const useBootstrap = () => useOutletContext<BootstrapCtx>();

const FarmShell = () => {
  const { farmId } = useParams<{ farmId: string }>();
  const { config, farms, user } = useBootstrap();

  const activeFarm = farms.find((f) => f.id === farmId) ?? farms[0];
  const farmSettings = useFarmSettings(activeFarm.id);

  const isAdmin = !!user?.authorities?.includes("ROLE_ADMIN");

  const resolved: ResolvedProps = useMemo(
    () => ({
      farmId: activeFarm.id,
      farmBbox: activeFarm.boundingBox,
      farms: farms.map((f) => ({
        id: f.id,
        name: f.name!,
        countryCode: activeFarm.countryCode,
        boundingBox: f.boundingBox,
      })),
      farm: {
        isAdmin,
        id: activeFarm.id,
        name: activeFarm.name!,
        countryCode: activeFarm.countryCode,
        boundingBox: activeFarm.boundingBox,
      },
      config,
      apiProps: {
        farmId: activeFarm.id,
        beOpts: config.api,
        langId: null,
      },
    }),
    [activeFarm, config, farms, isAdmin],
  );

  return (
    <FarmSettingsProvider settings={farmSettings}>
      <FeatureFlagProvider userId={resolved.farm.id}>
        <InitializeIntercom currentFarm={resolved.farm} farms={resolved.farms}>
          <NavbarWrapper
            ngProps={{
              isAdmin: resolved.farm.isAdmin,
              farm: resolved.farm,
              farms: resolved.farms,
              farmSettings: LocalStorage.loadFromLocalStorage("farmSettings"),
              langId: Localization.getLang(),
              langs: LANGUAGES,
              user: LocalStorage.loadFromLocalStorage("user"),
            }}
          >
            <Outlet context={resolved} />
          </NavbarWrapper>
        </InitializeIntercom>
      </FeatureFlagProvider>
    </FarmSettingsProvider>
  );
};

const useResolvedProps = () => useOutletContext<ResolvedProps>();

const withSuspense =
  (text: string, Comp: React.ComponentType<ResolvedProps>) => () => {
    const props = useResolvedProps();
    return (
      <Suspense fallback={<div>{text}</div>}>
        <Comp {...props} />
      </Suspense>
    );
  };

const ActionsRoute = withSuspense("Loading actions...", LazyActionsModule);
const ActionsV2Route = withSuspense("Loading actions...", LazyActionsV2Module);
const ReportsRoute = withSuspense("Loading reports...", LazyReportsModule);
const UserRoute = withSuspense("Loading user...", LazyUserModule);
const MapRoute = withSuspense("Loading map...", LazyMapModule);
const AdminRoute = withSuspense("Loading admin...", LazyAdminModule);
const NotificationsRoute = withSuspense(
  "Loading notifications...",
  LazyNotificationsModule,
);
const SensorsRoute = withSuspense("Loading sensors...", LazySensorsModule);
const CataloguesRoute = withSuspense(
  "Loading catalogues...",
  LazyCataloguesModule,
);
const IrrigationRoute = withSuspense("Loading irrigation...", IrrigationModule);
const StoresRoute = withSuspense("Loading stores...", LazyStoresModule);
const TelematicsRoute = withSuspense(
  "Loading telematics...",
  LazyTelematicsModule,
);
const ParcelsRoute = withSuspense("Loading parcels...", LazyParcelsModule);
const ParcelsV2Route = withSuspense("Loading parcels...", LazyParcelsV2Module);
const PrecisionFarmingRoute = withSuspense(
  "Loading precision farming...",
  LazyPrecisionFarmingModule,
);

const FarmIndexRedirect = () => {
  const { farms } = useBootstrap();
  return <Navigate replace to={`/farm/${farms[0]!.id}`} />;
};

const FarmIdRootRoute = () => {
  const props = useResolvedProps();
  return <Navigate replace to={`/farm/${props.farm.id}/map`} />;
};

const AppRoutes = () => (
  <InitializeLanguage>
    <InitializeSentry>
      <InitializeGoogleAnalytics>
        <QueryClientProvider client={queryClient}>
          <ErrorBoundary
            fallbackRender={({ error }) => <ChunkErrorFallback error={error} />}
          >
            <BrowserRouter>
              <Routes>
                <Route element={<LoginWrapper />} path="/login" />

                <Route
                  path="/reset"
                  element={
                    <ConfigResolver>
                      {(config) => (
                        <CfApp
                          apiProps={{ beOpts: config.api }}
                          config={config}
                          reducer={resetReducer}
                          render={(p: Record<string, unknown>) => (
                            <ResetWrapper {...p} />
                          )}
                        />
                      )}
                    </ConfigResolver>
                  }
                />

                <Route
                  path="/signup/*"
                  element={
                    <ConfigResolver>
                      {(config) => (
                        <CfApp
                          apiProps={{ beOpts: config.api }}
                          config={config}
                          reducer={signupReducer}
                          render={(p: Record<string, unknown>) => (
                            <SignupWrapper
                              langId={Localization.getLang()}
                              {...p}
                            />
                          )}
                        />
                      )}
                    </ConfigResolver>
                  }
                />

                <Route
                  path="/oauth/*"
                  element={
                    <Suspense fallback={<CfLoader size={20} />}>
                      <LazyOAuthModule />
                    </Suspense>
                  }
                />

                <Route element={<ResolverBootstrap />}>
                  <Route element={<Navigate replace to="/farm" />} path="/" />
                  <Route element={<FarmIndexRedirect />} path="/farm" />

                  <Route element={<FarmShell />} path="/farm/:farmId/*">
                    <Route element={<FarmIdRootRoute />} index />

                    <Route element={<ActionsRoute />} path="actions/*" />
                    <Route element={<ActionsV2Route />} path="actions-v2/*" />
                    <Route element={<ReportsRoute />} path="reports/*" />
                    <Route element={<UserRoute />} path="user/*" />
                    <Route element={<MapRoute />} path="map/*" />
                    <Route element={<AdminRoute />} path="admin/*" />
                    <Route
                      element={<NotificationsRoute />}
                      path="notifications/*"
                    />
                    <Route element={<SensorsRoute />} path="sensors/*" />
                    <Route element={<CataloguesRoute />} path="catalogues/*" />
                    <Route element={<IrrigationRoute />} path="irrigation/*" />
                    <Route element={<StoresRoute />} path="stores/*" />
                    <Route element={<TelematicsRoute />} path="telematics/*" />
                    <Route element={<ParcelsRoute />} path="parcels/*" />
                    <Route element={<ParcelsV2Route />} path="parcels-v2/*" />
                    <Route
                      element={<PrecisionFarmingRoute />}
                      path="precision-farming/*"
                    />
                  </Route>

                  <Route element={<Navigate replace to="/farm" />} path="*" />
                </Route>
              </Routes>
            </BrowserRouter>
          </ErrorBoundary>
        </QueryClientProvider>
      </InitializeGoogleAnalytics>
    </InitializeSentry>
  </InitializeLanguage>
);

const root = createRoot(document.querySelector("#app")!);
root.render(<AppRoutes />);
